import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import SendbirdChat from "@sendbird/chat";
import { GroupChannelModule } from "@sendbird/chat/groupChannel";
import { Field, Form, Formik } from "formik";
import { User } from "lib/features/user/types";
import { useState } from "react";
import "react-phone-number-input/style.css";
import chatServices from "services/chat-service";
import referralServices from "services/referral-service";
import { APP_ID } from "util/sendbirdHelper";
import * as Yup from "yup";
import CrossIcon from "../../assets/icons/cross.svg";
import ShareIcon from "../../assets/icons/share_icon.svg";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: { xs: "90%", sm: "680px" },
  maxHeight: "90%",
  overflowY: "auto",
  width: { xs: "90%", sm: "450px" },
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
  margin: "0px 0px",
  p: { xs: 2, sm: "26px" },
};

const validationSchema = Yup.object().shape({
  customerName: Yup.string()
    .required("Customer Name is required.")
    .min(2, "Customer Name must be at least 2 characters."),
  customerCell: Yup.string()
    .required("Customer Cell Number is required.")
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits.")
    .min(10, "Phone number must be exactly 10 digits"),
  additional_comments: Yup.string().optional(),
});

const initialValues = {
  customerName: "",
  customerCell: "",
  additional_comments: "",
};

const formatToE164 = (phone: string) => {
  const cleaned = phone.replace(/[^0-9]/g, "");
  return `+1${cleaned}`; // US country code
};

interface CustomerModalProps {
  open: boolean;
  close: () => void;
  user_id: number | undefined; // You can adjust the type based on your use case
  business_id: number | undefined;
  businessName?: string;
  referral_type: number;
  state: number;
  customStyle?: object;
  sx?: object;
}

export default function CustomerModal({
  open,
  close,
  user_id,
  business_id,
  businessName,
  customStyle = {},
  sx = {},
}: CustomerModalProps) {
  // Form state management
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const userData = localStorage.getItem("user") as string;
  const parsedUserObject = JSON.parse(userData) as User | undefined;
  const userId = parsedUserObject?.id;

  // Initialize SendbirdChat instance
  const initializeSendbird = async () => {
    try {
      if (userId) {
        // Initialize the SDK with your application ID
        const sb = await SendbirdChat.init({
          appId: APP_ID, // Replace with your actual app ID
          modules: [new GroupChannelModule()],
        });

        // Optionally, you can connect the user here
        await sb.connect(userId?.toString()); // Replace with actual user ID

        return sb;
      }
    } catch (error) {
      console.error("Error initializing Sendbird: ", error);
    }
  };

  const handleSubmit = async (values: {
    customerName: string;
    customerCell: string;
    additional_comments: string;
  }) => {
    if (!userId || !business_id) return;

    setLoading(true);

    try {
      console.log("Reached 1....");
      // Check if the channel URL already exists for the customer phone and user
      const existingChannelResponse = await chatServices.getChannelUrlByPhone(
        userId,
        formatToE164(values.customerCell)
      );

      console.log("Reached 2....");
      if (existingChannelResponse?.data?.data?.channel) {
        setError(
          `A customer with this phone number is already connected to ${businessName}`
        );
        setLoading(false);
        return;
      }

      let message = `${parsedUserObject?.first_name} ${parsedUserObject?.last_name} has connected you with ${businessName}. Feel free to ask any questions about their services! Reply STOP to unsubscribe at anytime.`;

      const sb = await initializeSendbird();

      if (sb) {
        const channel = await sb.groupChannel.createChannel({
          invitedUserIds: [userId?.toString()],
          operatorUserIds: [userId?.toString()],
        });

        const channelUrl = channel?.url;

        console.log("Reached 3....");
        await referralServices
          .createReferral({
            customer_name: values?.customerName,
            customer_phone: formatToE164(values?.customerCell),
            additional_comments: values?.additional_comments,
            channel: channelUrl,
            user_id: Number(userId),
            business_id: Number(business_id),
            state: 3,
            referral_type: 10,
            message: message,
          })
          .then(() => {
            setLoading(false);
            setSuccess(true);
            console.log("Reached 4....");
          })
          .catch((err) => {
            console.error(err);
            console.log("Reached 5....");
          });
      }
    } catch (error) {
      console.log({
        error,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      aria-labelledby="customer-modal-title"
      aria-describedby="customer-modal-description"
      sx={{ ...sx }}
    >
      <Box sx={{ ...style, ...customStyle }}>
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 600,
            textAlign: success ? "left" : "center",
            marginBottom: "8px",
          }}
        >
          {success
            ? `The Customer is now connected to ${businessName}!`
            : "Connect Customer"}
        </Typography>

        <img
          src={CrossIcon}
          width={16}
          height={16}
          onClick={() => {
            close();
            setSuccess(false);
          }}
          style={{
            cursor: "pointer",
            position: "absolute",
            top: "20px",
            right: "20px",
          }}
        />

        <Box>
          {success ? (
            <Typography sx={{ fontWeight: 400 }}>
              Your customer will now be able to communicate with the business.
              If a booking occurs, you will be notified of the referral and your
              payout amount.
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 400 }}>
              Fill out the below information to connect your Customer with{" "}
              <Typography sx={{ fontWeight: 700, display: "inline" }}>
                {businessName}
              </Typography>
            </Typography>
          )}
        </Box>

        {!success && (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, handleBlur, touched, errors }) => (
              <Form>
                <Box mt={2} display="block">
                  <Field
                    name="customerName"
                    as={TextField}
                    fullWidth
                    margin="normal"
                    label="Name"
                    required
                    placeholder="Enter customer name"
                    value={values.customerName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.customerName && Boolean(errors.customerName)}
                    helperText={touched.customerName && errors.customerName}
                    sx={{
                      marginBottom: 2,
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                  <Field
                    name="customerCell"
                    as={TextField}
                    fullWidth
                    margin="normal"
                    label="Phone Number"
                    required
                    type="tel"
                    placeholder="Enter phone number"
                    value={values.customerCell}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.customerCell && Boolean(errors.customerCell)}
                    helperText={touched.customerCell && errors.customerCell}
                    sx={{
                      marginBottom: 2,
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />

                  <Field
                    name="additional_comments"
                    as={TextField}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                    label="Comments"
                    placeholder="Only the business will see these notes"
                    sx={{
                      marginBottom: 2,
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                </Box>

                {error && (
                  <Typography
                    sx={{
                      color: "red", // Red text color
                      fontWeight: "bold", // Bold font weight
                      fontSize: "14px", // Font size
                      padding: "8px", // Padding around the text
                      backgroundColor: "#fff2f2", // Light red background for the message
                      borderRadius: "4px", // Rounded corners for the background
                      marginTop: "10px", // Space above the message
                      border: "1px solid red", // Red border around the message
                    }}
                  >
                    {error}
                  </Typography>
                )}

                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    sx={{
                      width: "80%",
                      margin: "0 auto",
                      display: "flex",
                      padding: "12px",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#328734",
                      "&:hover": {
                        backgroundColor: "#328734",
                      },
                      borderRadius: "8px",
                    }}
                    disabled={loading}
                    type="submit"
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      <>
                        <img
                          src={ShareIcon}
                          alt="ShareIcon"
                          style={{ marginRight: "8px" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "17px",
                            fontWeight: 600,
                            color: "#fff",
                            fontFamily: theme.typography.fontFamily,
                          }}
                        >
                          Connect Customer
                        </Typography>
                      </>
                    )}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
}
